// =============================================================================
// Module Theme Context - Thème dynamique Lapyno
// Applique les couleurs du module projet aux variables CSS
// =============================================================================

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

// Lapyno is a single-module SaaS (projects only)
// We keep the theme system for potential future expansion
type LapynoModule = 'projects' | 'admin';

// Module theme colors in HSL format (without hsl() wrapper for CSS variables)
const MODULE_THEMES: Record<LapynoModule, { h: number; s: number; l: number }> = {
  projects: { h: 217, s: 91, l: 60 },      // Blue #3B82F6
  admin: { h: 0, s: 72, l: 51 },           // Modern Red #DC2626
};

interface ModuleThemeContextType {
  currentModule: LapynoModule;
  setCurrentModule: (module: LapynoModule) => void;
  moduleColor: string;
}

const ModuleThemeContext = createContext<ModuleThemeContextType | null>(null);

export function ModuleThemeProvider({ children }: { children: React.ReactNode }) {
  const [currentModule, setCurrentModule] = useState<LapynoModule>('projects');
  const location = useLocation();

  // Detect module from route
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith('/admin')) {
      setCurrentModule('admin');
    } else {
      // Everything else is projects module
      setCurrentModule('projects');
    }
  }, [location.pathname]);

  // Apply theme to CSS variables
  useEffect(() => {
    const theme = MODULE_THEMES[currentModule];
    const root = document.documentElement;
    
    // Update primary color variables to module color
    root.style.setProperty('--module-primary', `${theme.h} ${theme.s}% ${theme.l}%`);
    root.style.setProperty('--module-primary-light', `${theme.h} ${theme.s}% ${Math.min(theme.l + 10, 95)}%`);
    root.style.setProperty('--module-primary-dark', `${theme.h} ${theme.s}% ${Math.max(theme.l - 15, 20)}%`);
    root.style.setProperty('--module-primary-glow', `${theme.h} ${theme.s}% ${theme.l}%`);
    
    // Add class to body for module-specific styling
    document.body.setAttribute('data-module', currentModule);
    
    return () => {
      document.body.removeAttribute('data-module');
    };
  }, [currentModule]);

  const moduleColor = `hsl(${MODULE_THEMES[currentModule].h} ${MODULE_THEMES[currentModule].s}% ${MODULE_THEMES[currentModule].l}%)`;

  // Batch 5A (Q3): memoize context value.
  const value = useMemo(
    () => ({ currentModule, setCurrentModule, moduleColor }),
    [currentModule, moduleColor],
  );

  return (
    <ModuleThemeContext.Provider value={value}>
      {children}
    </ModuleThemeContext.Provider>
  );
}

export function useModuleTheme() {
  const context = useContext(ModuleThemeContext);
  if (!context) {
    throw new Error('useModuleTheme must be used within ModuleThemeProvider');
  }
  return context;
}

// Hook to get module-aware primary color class
export function useModulePrimaryClass() {
  const { currentModule } = useModuleTheme();
  return `module-${currentModule}`;
}
